import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Organization, OrgUser } from '@newgenus/common';
import { Component, Inject, OnInit, OnDestroy, inject } from '@angular/core';
import { StaffPosition, staffPositionLines, staffPositions } from '../../shared/part-location';
import { OrgViewToggleComponent, OrgListViewService } from '../../shared/org-view-toggle';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../../material.module';
import { OrgUserDetailsDialogComponent } from '../org-user-details-dialog';
import { OrgDialogService } from '../../services/org-dialog.service';
import { OrgEntityColorsService } from '../../../org-dashboard/services/org-entity-colors.service';
import { ProfileColorService } from '../../services/profile-color.service';
import { OrgHattingService } from '../../services/org-hatting.service';
import { OrgFilterBarComponent, OrgFilterConfig } from '../../shared/org-filter-bar/org-filter-bar.component';
import { OrgSearchBarComponent, PositionSearchResult } from '../../shared/org-search-bar/org-search-bar.component';
import { expandTermToSubtreePostNames, fuzzyMatch } from '../../shared/org-filter-utils';
import { Subject, takeUntil, filter } from 'rxjs';
import { restrictToKeys } from '../../shared/only-keys';

export interface UsersListDialogData {
    organization: Organization;
    positionFilter?: string;
    /** Only these items (a staff member's dashboard tile, ISS-022); absent = all. */
    onlyKeys?: string[];
}

interface UserDisplayCard {
    uid: string;
    name: string;
    email?: string;
    hatsCount: number;
    primaryPosition?: string;
    orgUser: OrgUser;
    positionsCount: number;
    featuresCount: number;
    permissionsCount: number;
    securityGroupsCount: number;
    policiesCount: number;
    pendingPoliciesCount: number;
    positionNames: string[];
    /** ISS-021: "Head of Department – Div. 2 / Dept. 4 Promotion & Marketing" per post held. */
    positionLines: string[];
    /** The same posts for the card: name, where, and the offices under each (Paul, 28 Oct). */
    positions: StaffPosition[];
    hatNames: string[];
    sgNames: string[];
    isOnline: boolean;
    isOnPost: boolean;
    hasPendingApprovals: boolean;
    /** Phase B.3 — user has an outstanding invitation for this org (S1). */
    awaitingAcceptance?: boolean;
}

@Component({
    selector: 'shared-org-users-list-dialog',
    imports: [
        MaterialModule,
        CommonModule,
        FormsModule,
        OrgFilterBarComponent,
        OrgSearchBarComponent,
        OrgViewToggleComponent,
    ],
    template: `
        <h2 mat-dialog-title class="dialog-header">
            <mat-icon class="mr-2">group</mat-icon>
            Organization Staff ({{ filteredUsers.length }} of {{ allUsers.length }})

            <!-- User Explorer button -->
            <button
                mat-icon-button
                class="explorer-button"
                (click)="openUserExplorer()"
                matTooltip="Open Staff Explorer - View staff by hat, security group, or feature"
            >
                <mat-icon>account_tree</mat-icon>
            </button>

            <shared-org-view-toggle></shared-org-view-toggle>
            <button mat-icon-button mat-dialog-close class="close-button">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="dialog-content" [class.ng-list-view]="(listView.mode$ | async) === 'list'">
            <!-- Search Bar -->
            <shared-org-search-bar
                searchLabel="Search staff"
                searchPlaceholder="Search by name, email, hat, group..."
                [showPositionSearch]="true"
                [positionNames]="allPositionNames"
                [initialPositionTerm]="positionSearchTerm"
                (searchChanged)="onSearchChanged($event)"
                (positionSearchChanged)="onPositionSearchChanged($event)"
            >
            </shared-org-search-bar>

            <!-- Filters -->
            <shared-org-filter-bar
                [filters]="filterConfig"
                [rightFilters]="rightFilterConfig"
                activeColor="#43a047"
                activeDarkColor="#66bb6a"
                [totalCount]="allUsers.length"
                (filtersChanged)="onFiltersChanged($event)"
            >
            </shared-org-filter-bar>

            <!-- User card template (reused in both views) -->
            <ng-template #userCardTpl let-user>
                <mat-card
                    class="user-card"
                    [class.post-card]="user.isOnPost"
                    [class.pending-card]="user.hasPendingApprovals"
                    (dblclick)="onUserDoubleClick(user)"
                >
                    <div class="card-identity">
                        <div
                            class="user-avatar"
                            [ngStyle]="{ 'background-image': 'url(' + getUserAvatarUrl(user) + ')' }"
                        >
                            <span
                                class="presence-dot"
                                [class.online]="user.isOnline"
                                [title]="user.isOnline ? 'Online' : 'Offline'"
                            ></span>
                        </div>
                        <div class="identity-text">
                            <span class="user-name">{{ user.name | titlecase }}</span>
                            <span class="user-email" *ngIf="user.email" [title]="user.email">{{ user.email }}</span>
                            <div class="staff-positions" *ngIf="user.positions.length">
                                <div
                                    class="staff-post"
                                    *ngFor="let post of user.positions"
                                    [ngStyle]="{ 'border-left-color': entityColors.getColor('positions') }"
                                >
                                    <span class="post-name">{{ post.name }}</span>
                                    <span class="post-where" *ngIf="post.where">{{ post.where }}</span>
                                    <div class="post-also" *ngFor="let office of post.also">
                                        <span class="post-name"
                                            ><span class="also-label">Also</span> {{ office.name }}</span
                                        >
                                        <span class="post-where" *ngIf="office.where">{{ office.where }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Phase B.10 — render the two lifecycle indicators as INDEPENDENT icons
                             so they can co-exist (a user can be both Invited AND have pending hat
                             approvals queued up against them). Order: blue invited first, then orange
                             pending-approval, both right-aligned via margin-left:auto on the first one. -->
                        <mat-icon
                            class="pending-approval-icon pending-acceptance-icon"
                            *ngIf="user.awaitingAcceptance"
                            matTooltip="Invitation sent — waiting for the user to accept before approval is available."
                        >
                            mark_email_unread
                        </mat-icon>
                        <mat-icon
                            class="pending-approval-icon"
                            *ngIf="user.hasPendingApprovals"
                            matTooltip="Has pending hat approvals"
                        >
                            hourglass_empty
                        </mat-icon>
                    </div>
                    <div class="card-footer">
                        <div class="badges-row">
                            <span
                                class="badge"
                                matTooltip="Hats"
                                [ngStyle]="
                                    user.hatsCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('hats'),
                                              color: entityColors.getColor('hats'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">school</mat-icon>
                                {{ user.hatsCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Org Parts"
                                [ngStyle]="
                                    user.positionsCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('positions'),
                                              color: entityColors.getColor('positions'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">work</mat-icon>
                                {{ user.positionsCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Security Groups"
                                [ngStyle]="
                                    user.securityGroupsCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('securityGroups'),
                                              color: entityColors.getColor('securityGroups'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">security</mat-icon>
                                {{ user.securityGroupsCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Features"
                                [ngStyle]="
                                    user.featuresCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('features'),
                                              color: entityColors.getColor('features'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">extension</mat-icon>
                                {{ user.featuresCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Permissions"
                                [ngStyle]="
                                    user.permissionsCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('permissions'),
                                              color: entityColors.getColor('permissions'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">vpn_key</mat-icon>
                                {{ user.permissionsCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Policies"
                                [ngStyle]="
                                    user.policiesCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('policies'),
                                              color: entityColors.getColor('policies'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">policy</mat-icon>
                                {{ user.policiesCount }}
                            </span>
                        </div>
                        <div class="card-actions">
                            <button
                                mat-icon-button
                                matTooltip="View Details"
                                color="primary"
                                (click)="onViewUser(user); $event.stopPropagation()"
                            >
                                <mat-icon>visibility</mat-icon>
                            </button>
                            <button
                                mat-icon-button
                                matTooltip="Chat"
                                color="accent"
                                (click)="onChatUser(user); $event.stopPropagation()"
                            >
                                <mat-icon>chat</mat-icon>
                            </button>
                        </div>
                    </div>
                </mat-card>
            </ng-template>

            <!-- Grouped by Position view -->
            <ng-container *ngIf="groupByPosition">
                <div *ngFor="let group of positionGroups" class="position-group">
                    <div
                        class="group-header"
                        [ngStyle]="{ 'border-left-color': entityColors.getColor('positions') }"
                        (click)="togglePositionCollapse(group.position)"
                    >
                        <mat-icon class="collapse-icon" [class.collapsed]="collapsedPositions.has(group.position)"
                            >expand_more</mat-icon
                        >
                        <mat-icon [ngStyle]="{ color: entityColors.getColor('positions') }">work</mat-icon>
                        <span class="group-name">{{ group.position }}</span>
                        <span class="group-count">({{ group.users.length }})</span>
                    </div>
                    <div class="users-grid" *ngIf="!collapsedPositions.has(group.position)">
                        <ng-container *ngFor="let user of group.users">
                            <ng-container *ngTemplateOutlet="userCardTpl; context: { $implicit: user }"></ng-container>
                        </ng-container>
                    </div>
                </div>
                <div *ngIf="positionGroups.length === 0" class="empty-state">
                    <mat-icon class="empty-icon">account_tree</mat-icon>
                    <p>No org part groups to display</p>
                </div>
            </ng-container>

            <!-- Normal (ungrouped) view -->
            <div class="users-grid" *ngIf="!groupByPosition && filteredUsers.length > 0">
                <ng-container *ngFor="let user of filteredUsers">
                    <ng-container *ngTemplateOutlet="userCardTpl; context: { $implicit: user }"></ng-container>
                </ng-container>
            </div>

            <!-- Empty State -->
            <div *ngIf="!groupByPosition && filteredUsers.length === 0" class="empty-state">
                <mat-icon class="empty-icon">person_search</mat-icon>
                <p *ngIf="searchTerm">No staff found matching "{{ searchTerm }}"</p>
                <p
                    *ngIf="
                        !searchTerm &&
                        (noHatsFilter || pendingAcksFilter || noPostsFilter || pendingApprovalsFilter || postsFilter)
                    "
                >
                    No staff match the active filters
                </p>
                <p
                    *ngIf="
                        !searchTerm &&
                        !noHatsFilter &&
                        !pendingAcksFilter &&
                        !noPostsFilter &&
                        !pendingApprovalsFilter &&
                        !postsFilter
                    "
                >
                    No staff in this organization
                </p>
            </div>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close>Close</button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            .dialog-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin: 0;
                padding: 16px 24px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.12);
            }

            .explorer-button {
                color: #2196f3;
                margin-left: 8px;
            }

            .explorer-button:hover {
                background-color: rgba(33, 150, 243, 0.1);
            }

            .close-button {
                margin-left: 4px;
            }

            .dialog-content {
                padding: 16px 24px;
                min-height: 400px;
                max-height: 70vh;
                overflow-y: auto;
            }

            .w-100 {
                width: 100%;
            }

            .users-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                gap: 16px;
            }

            .user-card {
                cursor: pointer;
                transition:
                    transform 0.2s,
                    box-shadow 0.2s;
                border: 1px solid rgba(0, 0, 0, 0.08);
                display: flex;
                flex-direction: column;
                padding: 12px 16px;
                background-color: #f5f5f5;
            }

            .user-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                border-color: rgba(0, 0, 0, 0.25);
            }

            .user-card.post-card {
                background-color: rgba(33, 150, 243, 0.08);
                border-color: rgba(33, 150, 243, 0.2);
            }

            .user-card.post-card:hover {
                border-color: rgba(33, 150, 243, 0.4);
            }

            .user-card.pending-card {
                border-color: rgba(255, 152, 0, 0.35);
                border-style: dashed;
            }

            .user-card.pending-card:hover {
                border-color: rgba(255, 152, 0, 0.55);
            }

            .pending-approval-icon {
                color: #ff9800;
                font-size: 20px;
                width: 20px;
                height: 20px;
                flex-shrink: 0;
                margin-left: auto;
            }

            /* When both lifecycle icons render, only the first one (left-most)
           needs the auto-margin push; the sibling sits flush next to it. */
            .pending-approval-icon + .pending-approval-icon {
                margin-left: 4px;
            }

            /* Phase B.10 — Pending Acceptance (S1) variant: blue mail-pending
           glyph to clearly distinguish from the orange Pending Approval (S2)
           hourglass. Matches the blue INVITED badge on the org-board tile. */
            .pending-approval-icon.pending-acceptance-icon {
                color: #1565c0;
                font-size: 22px;
                width: 22px;
                height: 22px;
            }

            /* Identity section: avatar + name/email */
            .card-identity {
                display: flex;
                /* Top: a card with several posts is tall, and the avatar sits by the name. */
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 12px;
            }

            .user-avatar {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                background-size: cover;
                background-position: center;
                position: relative;
                flex-shrink: 0;
            }

            .identity-text {
                display: flex;
                flex-direction: column;
                overflow: hidden;
                min-width: 0;
            }

            .user-name {
                font-size: 15px;
                font-weight: 600;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .user-email {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.55);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            /* Each post is its own block: name, then where it sits, then its offices (28 Oct). */
            .staff-positions {
                display: flex;
                flex-direction: column;
                gap: 6px;
                margin-top: 6px;
            }
            .staff-post {
                display: flex;
                flex-direction: column;
                border-left: 3px solid;
                padding-left: 8px;
                min-width: 0;
            }
            .post-name {
                font-size: 13px;
                font-weight: 500;
                line-height: 1.3;
                color: rgba(0, 0, 0, 0.8);
            }
            .post-where {
                font-size: 12px;
                line-height: 1.3;
                color: rgba(0, 0, 0, 0.55);
            }
            .post-also {
                display: flex;
                flex-direction: column;
                margin-top: 3px;
                padding-left: 10px;
            }
            .post-also .post-name {
                font-weight: 400;
            }
            .also-label {
                font-size: 11px;
                font-weight: 600;
                letter-spacing: 0.03em;
                text-transform: uppercase;
                color: rgba(0, 0, 0, 0.45);
            }

            /* Footer: badges + action buttons on same row */
            .card-footer {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                border-top: 1px solid rgba(0, 0, 0, 0.06);
                padding-top: 8px;
            }

            .card-actions {
                display: flex;
                flex-shrink: 0;
                align-items: center;
            }

            .card-actions ::ng-deep .mat-mdc-icon-button {
                width: 32px;
                height: 32px;
                padding: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
            }

            .card-actions ::ng-deep .mat-mdc-icon-button .mat-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
            }

            .card-actions ::ng-deep .mat-mdc-icon-button .mat-mdc-button-persistent-ripple,
            .card-actions ::ng-deep .mat-mdc-icon-button .mat-mdc-focus-indicator {
                width: 32px;
                height: 32px;
            }

            .presence-dot {
                position: absolute;
                bottom: -2px;
                right: -2px;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: #757575;
                border: 2px solid #fff;
                transition: background 0.3s;

                &.online {
                    background: #43a047;
                    box-shadow: 0 0 6px rgba(67, 160, 71, 0.6);
                    animation: presencePulse 2s ease-in-out infinite;
                }
            }

            @keyframes presencePulse {
                0%,
                100% {
                    box-shadow: 0 0 6px rgba(67, 160, 71, 0.6);
                }
                50% {
                    box-shadow: 0 0 12px rgba(67, 160, 71, 0.9);
                }
            }

            .badges-row {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
                min-width: 0;
            }

            .badge {
                display: inline-flex;
                align-items: center;
                gap: 2px;
                padding: 2px 6px;
                border-radius: 12px;
                font-size: 12px;
                background-color: rgba(0, 0, 0, 0.06);
                color: rgba(0, 0, 0, 0.5);
            }

            /* Badge active colors now applied via [ngStyle] from OrgEntityColorsService */

            .badge-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 48px;
                color: rgba(0, 0, 0, 0.54);
            }

            .empty-icon {
                font-size: 64px;
                width: 64px;
                height: 64px;
                margin-bottom: 16px;
            }

            .mr-2 {
                margin-right: 8px;
            }

            ::ng-deep .user-card .mat-mdc-card-header-text {
                overflow: hidden;
            }

            /* Dark theme overrides */
            :host-context(.dark-theme) .user-card {
                background-color: #383838;
                border-color: rgba(255, 255, 255, 0.1);
            }
            :host-context(.dark-theme) .user-card:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
                border-color: rgba(255, 255, 255, 0.2);
            }
            :host-context(.dark-theme) .user-card.post-card {
                background-color: rgba(33, 150, 243, 0.12);
                border-color: rgba(33, 150, 243, 0.25);
            }
            :host-context(.dark-theme) .user-card.post-card:hover {
                border-color: rgba(33, 150, 243, 0.45);
            }
            :host-context(.dark-theme) .user-card.pending-card {
                border-color: rgba(255, 152, 0, 0.4);
            }
            :host-context(.dark-theme) .user-card.pending-card:hover {
                border-color: rgba(255, 152, 0, 0.6);
            }
            :host-context(.dark-theme) .user-email,
            :host-context(.dark-theme) .post-where,
            :host-context(.dark-theme) .also-label {
                color: rgba(255, 255, 255, 0.55);
            }
            :host-context(.dark-theme) .post-name {
                color: rgba(255, 255, 255, 0.85);
            }
            :host-context(.dark-theme) .card-footer {
                border-top-color: rgba(255, 255, 255, 0.08);
            }
            :host-context(.dark-theme) .presence-dot {
                border-color: #424242;
            }
            :host-context(.dark-theme) .badge {
                background-color: rgba(255, 255, 255, 0.1);
                color: rgba(255, 255, 255, 0.5);
            }
            :host-context(.dark-theme) .empty-state {
                color: rgba(255, 255, 255, 0.54);
            }

            /* Position group layout */
            .position-group {
                margin-bottom: 24px;
            }

            .group-header {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                margin-bottom: 12px;
                border-left: 3px solid;
                background: rgba(0, 0, 0, 0.03);
                border-radius: 0 4px 4px 0;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                user-select: none;
            }

            .group-header:hover {
                background: rgba(0, 0, 0, 0.06);
            }

            .collapse-icon {
                transition: transform 0.2s;
                color: rgba(0, 0, 0, 0.4);
                font-size: 20px;
                width: 20px;
                height: 20px;
            }

            .collapse-icon.collapsed {
                transform: rotate(-90deg);
            }

            .group-name {
                font-weight: 600;
            }

            .group-count {
                color: rgba(0, 0, 0, 0.45);
                font-weight: 400;
            }

            /* (dark theme secondary-filter-row merged into filter-row above) */
            :host-context(.dark-theme) .group-header {
                background: rgba(255, 255, 255, 0.04);
            }
            :host-context(.dark-theme) .group-header:hover {
                background: rgba(255, 255, 255, 0.08);
            }
            :host-context(.dark-theme) .collapse-icon {
                color: rgba(255, 255, 255, 0.4);
            }
            :host-context(.dark-theme) .group-count {
                color: rgba(255, 255, 255, 0.45);
            }

            /* ── Phones ────────────────────────────────────────────────────────────
           The dialog is full-screen (see global .ob-list-dialog rules), so tighten
           the chrome and let cards use a single full-width column instead of the
           280px-min grid that overflows a narrow viewport. */
            @media (max-width: 767.98px) {
                .dialog-header {
                    padding: 12px 16px;
                    font-size: 18px;
                }
                .dialog-content {
                    padding: 12px 16px;
                    min-height: 0;
                }
                /* minmax(0,1fr) (not 1fr) removes the implicit min-content floor so the
               card actually shrinks to the viewport instead of overflowing right. */
                .users-grid {
                    grid-template-columns: minmax(0, 1fr);
                    gap: 12px;
                }
                .users-grid > * {
                    min-width: 0;
                    max-width: 100%;
                }
                .card-identity,
                .identity-text {
                    min-width: 0;
                }
                /* Badges take a full row (wrapping internally), actions drop below. */
                .card-footer {
                    flex-wrap: wrap;
                }
                .badges-row {
                    flex: 1 1 100%;
                }
                .card-actions {
                    margin-left: auto;
                }
            }
        `,
    ],
})
export class OrgUsersListDialogComponent implements OnInit, OnDestroy {
    public entityColors = inject(OrgEntityColorsService);
    public listView = inject(OrgListViewService);
    public searchTerm = '';
    public positionSearchTerm = '';
    public matchedPositionNames: string[] = [];
    public allUsers: UserDisplayCard[] = [];
    public filteredUsers: UserDisplayCard[] = [];
    public onlineFilter: 'all' | 'online' | 'offline' = 'all';
    public onlineCount = 0;
    public noHatsFilter = false;
    public pendingAcksFilter = false;
    public postsFilter = false;
    public noPostsFilter = false;
    public pendingApprovalsFilter = false;
    public groupByPosition = false;
    public noHatsCount = 0;
    public pendingAcksCount = 0;
    public postsCount = 0;
    public noPostsCount = 0;
    public pendingApprovalsCount = 0;
    public allPositionNames: string[] = [];
    public filterConfig: OrgFilterConfig[] = [];
    public rightFilterConfig: OrgFilterConfig[] = [];
    public positionGroups: { position: string; users: UserDisplayCard[] }[] = [];
    public collapsedPositions = new Set<string>();
    private destroy$ = new Subject<void>();
    private profileColorService = inject(ProfileColorService);
    private orgHattingService = inject(OrgHattingService);

    constructor(
        public dialogRef: MatDialogRef<OrgUsersListDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: UsersListDialogData,
        private dialog: MatDialog,
        private dialogService: OrgDialogService
    ) {}

    public ngOnInit(): void {
        // Set initial position filter if provided
        if (this.data?.positionFilter) {
            this.positionSearchTerm = this.data.positionFilter;
        }
        // Load initial data
        this.loadUsers();

        // Subscribe to live organization updates so the user list refreshes automatically
        this.dialogService.organization$
            .pipe(
                takeUntil(this.destroy$),
                filter((org) => !!org)
            )
            .subscribe((org) => {
                this.data = { ...this.data, organization: org! };
                this.loadUsers();
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadUsers(): void {
        const org = this.data.organization;
        const onlineSet = new Set(this.dialogService.onlineUserIds || []);
        console.log('loadUsers > org:', org);
        console.log('loadUsers > orgUsers:', org?.orgUsers);
        console.log('loadUsers > userKeys:', org?.orgUsers?.userKeys);

        if (!org?.orgUsers?.userKeys) {
            console.warn('No userKeys found on organization.orgUsers');
            this.allUsers = [];
            this.filteredUsers = [];
            return;
        }

        this.allUsers = restrictToKeys(org.orgUsers.userKeys as string[], this.data.onlyKeys, (k) => k).map(
            (uid: string) => {
                const orgUser = org.orgUsers[uid] as OrgUser;
                console.log('Processing user:', uid, orgUser);

                // Get security groups from orgSecurityGroups on the organization
                const orgSecurityGroups = (org as any).orgSecurityGroups || {};

                // Collect security group keys from hatsDetails
                const hatsDetails = orgUser?.hatsDetails || {};
                const securityGroupKeys = new Set<string>();

                // hatsDetails is an object keyed by hat key
                if (hatsDetails && typeof hatsDetails === 'object') {
                    Object.values(hatsDetails).forEach((hatDetail: any) => {
                        // securityGroups can be array of objects with key or array of strings
                        const sgRefs = hatDetail?.securityGroups || hatDetail?.securityGroupsArray || [];
                        if (Array.isArray(sgRefs)) {
                            sgRefs.forEach((sgRef: any) => {
                                const sgKey = typeof sgRef === 'string' ? sgRef : sgRef?.key;
                                if (sgKey) securityGroupKeys.add(sgKey);
                            });
                        }
                    });
                }

                // Also check direct securityGroupsDetails on user
                const userSgDetails = (orgUser as any)?.securityGroupsDetails || {};
                Object.keys(userSgDetails).forEach((key) => securityGroupKeys.add(key));

                // Now count features from the security groups
                const features = new Set<string>();
                securityGroupKeys.forEach((sgKey) => {
                    const sg = orgSecurityGroups[sgKey];
                    if (sg?.features && Array.isArray(sg.features)) {
                        sg.features.forEach((f: string) => features.add(f));
                    }
                });

                // Also add direct user features
                const userFeatures = (orgUser as any)?.features || [];
                if (Array.isArray(userFeatures)) {
                    userFeatures.forEach((f: string) => features.add(f));
                }

                // Count permissions from featureDetails (aggregated on user)
                let permissionsCount = 0;
                const featureDetails = (orgUser as any)?.featureDetails || {};
                Object.values(featureDetails).forEach((fd: any) => {
                    if (fd?.permissions && Array.isArray(fd.permissions)) {
                        permissionsCount += fd.permissions.length;
                    }
                });

                // Count positions (hats are linked to positions)
                const positionsCount = orgUser?.hats?.length || 0;

                // Count policies - check org.orgPolicies for policies targeting this user
                const orgPolicies = (org as any).orgPolicies || {};
                const policiesKeys = orgPolicies.policiesKeys || [];
                let policiesCount = 0;
                let pendingPoliciesCount = 0;
                policiesKeys.forEach((policyKey: string) => {
                    const policy = orgPolicies[policyKey];
                    if (policy?.targets?.affectedUserUids?.includes(uid)) {
                        policiesCount++;
                        // Count policies requiring acknowledgment from this user
                        if (policy?.requiresAcknowledgment) {
                            pendingPoliciesCount++;
                        }
                    }
                });

                // Resolve position names from hats
                const positionNames: string[] = [];
                const orgHats = org.orgHats || ({} as any);
                const userHatKeys = orgUser?.hats || [];
                userHatKeys.forEach((hatKey: string) => {
                    const hat = orgHats[hatKey];
                    if (hat?.positions && Array.isArray(hat.positions)) {
                        hat.positions.forEach((pos: any) => {
                            if (pos?.name && !positionNames.includes(pos.name)) {
                                positionNames.push(pos.name);
                            }
                        });
                    }
                });

                // Resolve hat names for search
                const hatNames: string[] = userHatKeys
                    .map((hatKey: string) => {
                        const hat = orgHats[hatKey];
                        return hat && typeof hat === 'object' ? hat.name || '' : '';
                    })
                    .filter((n: string) => !!n);

                // Resolve security group names for search
                const sgNames: string[] = Array.from(securityGroupKeys)
                    .filter((k) => orgSecurityGroups[k])
                    .map((k) => orgSecurityGroups[k].name || '');

                return {
                    uid,
                    name: orgUser?.name || orgUser?.userName || 'Unknown User',
                    email: orgUser?.email || '', // Email from orgUser (synced from user profile)
                    hatsCount: orgUser?.hats?.length || 0,
                    primaryPosition: positionNames[0] || this.findPrimaryPosition(uid, org),
                    orgUser,
                    positionsCount,
                    featuresCount: features.size,
                    permissionsCount,
                    securityGroupsCount: securityGroupKeys.size,
                    policiesCount,
                    pendingPoliciesCount,
                    positionNames,
                    positionLines: staffPositionLines(org, uid),
                    positions: staffPositions(org, uid),
                    hatNames,
                    sgNames,
                    isOnline: onlineSet.has(uid),
                    isOnPost: false,
                    hasPendingApprovals: false,
                };
            }
        );

        // Compute isOnPost for each user
        const orgParts = org?.orgParts || [];
        const allOrgHats = org?.orgHats || ({} as any);
        this.allUsers.forEach((user) => {
            const userHatKeys = user.orgUser?.hats || [];
            user.isOnPost = userHatKeys.some((hatKey: string) => {
                const hat = allOrgHats[hatKey] as any;
                if (!hat?.positionArray) return false;
                return hat.positionArray.some((partKey: string) => {
                    const part = orgParts.find((p: any) => p.key === partKey || p.partKey === partKey);
                    if (!part) return false;
                    if (part.partType) return part.partType.toLowerCase() === 'post';
                    return (part.depth || 0) >= 6;
                });
            });
        });

        console.log('loadUsers > allUsers:', this.allUsers);
        this.onlineCount = this.allUsers.filter((u) => u.isOnline).length;
        this.noHatsCount = this.allUsers.filter((u) => u.hatsCount === 0).length;
        this.pendingAcksCount = this.allUsers.filter((u) => u.pendingPoliciesCount > 0).length;
        this.postsCount = this.allUsers.filter((u) => u.isOnPost).length;
        this.noPostsCount = this.allUsers.filter((u) => !u.isOnPost).length;

        // Compute hasPendingApprovals for each user
        this.allUsers.forEach((user) => {
            const userUid = user.uid;
            user.hasPendingApprovals = Object.values(allOrgHats).some((hat: any) => {
                const pendingArr: string[] = hat?.pendingAssignedToArray || [];
                return pendingArr.includes(userUid);
            });
        });
        this.pendingApprovalsCount = this.allUsers.filter((u) => u.hasPendingApprovals).length;
        // Search-bar universe: names of positions users hold PLUS every org
        // part name — so a container term (the context menu pre-fills the
        // right-clicked part's name) resolves instead of reporting
        // "No org parts matching".
        const orgPartNames = (((org as any).orgParts ?? []) as Array<{ name?: string }>)
            .map((p) => p.name)
            .filter((n): n is string => !!n);
        this.allPositionNames = Array.from(
            new Set([...this.allUsers.flatMap((u) => u.positionNames), ...orgPartNames])
        ).sort();
        this.buildFilterConfigs();
        // Sort by name
        this.allUsers.sort((a, b) => a.name.localeCompare(b.name));
        this.filterUsers();

        // Ensure profile colors are loaded for all users
        this.profileColorService.ensureLoadedMany(this.allUsers.map((u) => u.uid));

        // Phase B.3 \u2014 tag users with outstanding invitations so the template can
        // render a distinct "Pending Acceptance" indicator (S1) vs "Pending
        // Approval" (S2). Async, fire-and-forget; UI re-renders via flag mutation.
        // Phase B.10 — derive awaitingAcceptance from the canonical
        // `orgUsers[uid].status === 'pending'` signal (same source the
        // org-board tile and user-details dialog use). The previous
        // implementation queried /invitations directly, which produced
        // false positives when invitation docs were left behind after
        // the user had already accepted.
        this.allUsers.forEach((u) => {
            u.awaitingAcceptance = (u.orgUser as any)?.status === 'pending';
        });
    }

    private findPrimaryPosition(uid: string, org: Organization): string {
        // Position information is derived from hats which link users to positions
        // For now, return empty - can be enhanced later to traverse hats → positions
        // The hatsDetails on orgUser contains security groups, not direct position info
        return '';
    }

    private buildFilterConfigs(): void {
        this.filterConfig = [
            { key: 'online', label: 'Online', icon: 'wifi', count: this.onlineCount },
            { key: 'offline', label: 'Offline', icon: 'wifi_off', count: this.allUsers.length - this.onlineCount },
            { key: 'noHats', label: 'No Hats', icon: 'person_off', count: this.noHatsCount },
            { key: 'pendingAcks', label: 'Pending Acks', icon: 'pending_actions', count: this.pendingAcksCount },
            { key: 'posts', label: 'Posts', icon: 'work', count: this.postsCount },
            { key: 'noPosts', label: 'No Posts', icon: 'work_off', count: this.noPostsCount },
            {
                key: 'pendingApprovals',
                label: 'Pending Approvals',
                icon: 'hourglass_empty',
                count: this.pendingApprovalsCount,
            },
        ];
        this.rightFilterConfig = [{ key: 'groupByPosition', label: 'By Org Part', icon: 'account_tree' }];
    }

    public onSearchChanged(term: string): void {
        this.searchTerm = term;
        this.filterUsers();
    }

    public onPositionSearchChanged(result: PositionSearchResult): void {
        this.positionSearchTerm = result.term;
        this.matchedPositionNames = result.matchedNames;
        this.filterUsers();
    }

    public onFiltersChanged(activeFilters: Set<string>): void {
        this.onlineFilter = activeFilters.has('online') ? 'online' : activeFilters.has('offline') ? 'offline' : 'all';
        this.noHatsFilter = activeFilters.has('noHats');
        this.pendingAcksFilter = activeFilters.has('pendingAcks');
        this.postsFilter = activeFilters.has('posts');
        this.noPostsFilter = activeFilters.has('noPosts');
        this.pendingApprovalsFilter = activeFilters.has('pendingApprovals');

        const newGroupByPosition = activeFilters.has('groupByPosition');
        if (newGroupByPosition !== this.groupByPosition) {
            this.groupByPosition = newGroupByPosition;
            if (!this.groupByPosition) {
                this.collapsedPositions.clear();
            }
        }

        this.filterUsers();
    }

    public togglePositionCollapse(position: string): void {
        if (this.collapsedPositions.has(position)) {
            this.collapsedPositions.delete(position);
        } else {
            this.collapsedPositions.add(position);
        }
    }

    public filterUsers(): void {
        let result = [...this.allUsers];

        // Apply online filter
        if (this.onlineFilter === 'online') {
            result = result.filter((u) => u.isOnline);
        } else if (this.onlineFilter === 'offline') {
            result = result.filter((u) => !u.isOnline);
        }

        // Apply no-hats filter
        if (this.noHatsFilter) {
            result = result.filter((u) => u.hatsCount === 0);
        }

        // Apply pending-acks filter
        if (this.pendingAcksFilter) {
            result = result.filter((u) => u.pendingPoliciesCount > 0);
        }

        // Apply posts filter
        if (this.postsFilter) {
            result = result.filter((u) => u.isOnPost);
        }

        // Apply no-posts filter
        if (this.noPostsFilter) {
            result = result.filter((u) => !u.isOnPost);
        }

        // Apply pending approvals filter
        if (this.pendingApprovalsFilter) {
            result = result.filter((u) => u.hasPendingApprovals);
        }

        // Apply search across name, email, positions, hats, security groups
        if (this.searchTerm.trim()) {
            const term = this.searchTerm.toLowerCase();
            result = result.filter(
                (user) =>
                    user.name.toLowerCase().includes(term) ||
                    (user.email && user.email.toLowerCase().includes(term)) ||
                    (user.primaryPosition && user.primaryPosition.toLowerCase().includes(term)) ||
                    user.positionLines.some((l) => l.toLowerCase().includes(term)) ||
                    user.positionNames.some((p) => p.toLowerCase().includes(term)) ||
                    user.hatNames.some((h) => h.toLowerCase().includes(term)) ||
                    user.sgNames.some((sg) => sg.toLowerCase().includes(term))
            );
        }

        // Position search (fuzzy). A term can also name a CONTAINER part
        // (division/department/section/unit — e.g. the context menu pre-fills
        // the right-clicked part's name); users hold POSTS, so expand matched
        // containers to their subtree's post names or a container term can
        // never match anyone.
        if (this.positionSearchTerm.trim()) {
            const subtreePosts = this.expandPositionTermToSubtreePosts(this.positionSearchTerm);
            result = result.filter((user) =>
                user.positionNames.some((pos) => fuzzyMatch(pos, this.positionSearchTerm) || subtreePosts.has(pos))
            );
        }

        this.filteredUsers = result;

        // Build position groups if grouping is active
        if (this.groupByPosition) {
            this.buildPositionGroups();
        }
    }

    /**
     * Post names under any org part whose name fuzzy-matches the term —
     * pure logic in `org-filter-utils.expandTermToSubtreePostNames`.
     */
    public expandPositionTermToSubtreePosts(term: string): Set<string> {
        return expandTermToSubtreePostNames((this.data.organization as any)?.orgParts ?? [], term);
    }

    private buildPositionGroups(): void {
        const groupMap = new Map<string, UserDisplayCard[]>();

        this.filteredUsers.forEach((user) => {
            if (user.positionNames.length === 0) {
                const key = 'No Org Part';
                if (!groupMap.has(key)) groupMap.set(key, []);
                groupMap.get(key)!.push(user);
            } else {
                user.positionNames.forEach((pos) => {
                    if (!groupMap.has(pos)) groupMap.set(pos, []);
                    groupMap.get(pos)!.push(user);
                });
            }
        });

        this.positionGroups = Array.from(groupMap.entries())
            .map(([position, users]) => ({ position, users }))
            .sort((a, b) => {
                // "No Position" goes last
                if (a.position === 'No Org Part') return 1;
                if (b.position === 'No Org Part') return -1;
                return a.position.localeCompare(b.position);
            });

        // Collapse all groups by default (only on first build, don't reset user toggles)
        if (this.collapsedPositions.size === 0 && this.positionGroups.length > 0) {
            this.positionGroups.forEach((g) => this.collapsedPositions.add(g.position));
        }
    }

    public encodeURL(name: string): string {
        return encodeURIComponent(name);
    }

    /**
     * Gets the avatar URL for a user.
     * Returns photoThumbnail if available, otherwise falls back to generated avatar.
     */
    public getUserAvatarUrl(user: UserDisplayCard): string {
        const org = this.data.organization;

        // Check orgUser for photoThumbnail
        if (user.orgUser?.photoThumbnail) {
            return user.orgUser.photoThumbnail;
        }

        // Check organization's orgUsers for most up-to-date data
        if (org?.orgUsers?.[user.uid]?.photoThumbnail) {
            return org.orgUsers[user.uid].photoThumbnail!;
        }

        // Fall back to generated avatar using ProfileColorService cache
        return this.profileColorService.getAvatarUrl(user.uid, user.name, 48);
    }

    public onViewUser(user: UserDisplayCard): void {
        // Open user details dialog WITHOUT closing the users list
        this.openUserDetailsDialog(user);
    }

    public onChatUser(user: UserDisplayCard): void {
        // TODO: Implement chat functionality
        console.log('Chat with user:', user.name);
        this.dialogRef.close({ action: 'chat', user: user.orgUser, uid: user.uid });
    }

    public onUserDoubleClick(user: UserDisplayCard): void {
        // Open user details dialog WITHOUT closing the users list
        this.openUserDetailsDialog(user);
    }

    private openUserDetailsDialog(user: UserDisplayCard): void {
        const userForDialog = {
            ...user.orgUser,
            key: user.uid,
            name: user.name,
        };

        this.dialog.open(OrgUserDetailsDialogComponent, {
            data: {
                node: null,
                user: userForDialog,
                organization: this.data.organization,
                onlineUserIds: this.dialogService.onlineUserIds,
            },
            height: '85%',
            width: '85%',
            maxHeight: 'unset',
            maxWidth: 'unset',
        });
    }

    public openUserExplorer(): void {
        import('../org-user-explorer-dialog/org-user-explorer-dialog').then((m) => {
            this.dialog.open(m.OrgUserExplorerDialogComponent, {
                width: '90%',
                height: '90vh',
                maxWidth: '1400px',
                maxHeight: '900px',
                data: {
                    organization: this.data.organization,
                },
                panelClass: ['user-explorer-dialog', 'ob-list-dialog'],
            });
        });
    }
}
